import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, toHexColor } from '../config/gameConfig';
import { COPY } from '../content/copy';
import { AudioManager } from '../systems/AudioManager';
import { SaveStore } from '../systems/SaveStore';

export type RunMode = 'core' | 'endless';

type Character = 'blue' | 'pink';

const MENU = {
  bgTop: 0x1b2b57,
  bgBottom: 0x111827,
  floor: 0x2c7a7b,
  counter: 0xf59e0b,
  counterDark: 0xb86c13,
  paper: 0xffffff,
  ink: 0x102033,
  muted: 0xcad7e3,
  button: 0xffcf3f,
  buttonHover: 0xffdd67,
  buttonText: 0x271a00,
  panel: 0x14223a,
  panelStroke: 0x4dd2cf,
};

export class MenuScene extends Phaser.Scene {
  private selectedCharacter: Character = 'blue';
  private characterButtons = new Map<Character, Phaser.GameObjects.Container>();

  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    this.createBackground();
    this.createLogo();
    this.createCharacterSelect();
    this.createModeCards();
    this.createDecorations();
    this.animateEntrance();

    AudioManager.getInstance().resumeContext();
  }

  private createBackground(): void {
    const bg = this.add.graphics();
    bg.fillGradientStyle(MENU.bgTop, MENU.bgTop, MENU.bgBottom, MENU.bgBottom, 1);
    bg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    bg.fillStyle(0x0f172a, 0.52);
    bg.fillRoundedRect(46, 88, GAME_WIDTH - 92, GAME_HEIGHT - 128, 28);
    bg.lineStyle(2, 0x31486a, 0.9);
    bg.strokeRoundedRect(46, 88, GAME_WIDTH - 92, GAME_HEIGHT - 128, 28);

    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT - 110, GAME_WIDTH - 150, 110, MENU.floor, 0.22)
      .setStrokeStyle(2, 0x71efe9, 0.25);
  }

  private createLogo(): void {
    const stamp = this.add.container(GAME_WIDTH / 2, 118).setName('intro');
    const badge = this.add.graphics();
    badge.fillStyle(0xffcf3f, 1);
    badge.fillRoundedRect(-56, -24, 112, 48, 18);
    badge.lineStyle(4, 0x111827, 1);
    badge.strokeRoundedRect(-56, -24, 112, 48, 18);
    stamp.add(badge);
    stamp.add(this.add.text(0, 0, 'AR', {
      fontFamily: FONTS.ui,
      fontSize: '28px',
      color: toHexColor(MENU.buttonText),
      fontStyle: '900',
    }).setOrigin(0.5));

    this.add.text(GAME_WIDTH / 2, 188, COPY.menu.title, {
      fontFamily: FONTS.ui,
      fontSize: '76px',
      color: '#ffffff',
      fontStyle: '900',
      stroke: '#102033',
      strokeThickness: 8,
    }).setOrigin(0.5).setName('intro');

    this.add.text(GAME_WIDTH / 2, 252, 'Accounts Receivable, but playable', {
      fontFamily: FONTS.ui,
      fontSize: '24px',
      color: '#9ee7e1',
      fontStyle: '800',
    }).setOrigin(0.5).setName('intro');

    this.add.text(GAME_WIDTH / 2, 285, 'Pick up work. Run it to the right station. Learn the AR loop by doing it.', {
      fontFamily: FONTS.ui,
      fontSize: '15px',
      color: toHexColor(MENU.muted),
    }).setOrigin(0.5).setName('intro');
  }

  private createCharacterSelect(): void {
    const y = 398;
    this.add.text(GAME_WIDTH / 2, y - 78, 'Choose your clerk', {
      fontFamily: FONTS.ui,
      fontSize: '15px',
      color: '#ffffff',
      fontStyle: '800',
    }).setOrigin(0.5).setName('intro');

    this.characterButtons.set('blue', this.createCharacterButton(GAME_WIDTH / 2 - 76, y, 'blue'));
    this.characterButtons.set('pink', this.createCharacterButton(GAME_WIDTH / 2 + 76, y, 'pink'));
    this.refreshCharacterSelect();
  }

  private createCharacterButton(x: number, y: number, character: Character): Phaser.GameObjects.Container {
    const container = this.add.container(x, y).setName('intro');
    const ring = this.add.circle(0, 0, 47, 0x0f172a, 1)
      .setStrokeStyle(3, 0x4dd2cf, 0.7);
    ring.setName('ring');
    container.add(ring);
    this.drawCharacter(container, character, 0, -4, 1.15);
    const label = character === 'blue' ? 'Blue' : 'Pink';
    container.add(this.add.text(0, 62, label, {
      fontFamily: FONTS.ui,
      fontSize: '13px',
      color: '#ffffff',
      fontStyle: '800',
    }).setOrigin(0.5));

    const hit = this.add.circle(0, 0, 54, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    container.add(hit);
    hit.on('pointerover', () => this.tweens.add({ targets: container, scale: 1.08, duration: 100 }));
    hit.on('pointerout', () => this.tweens.add({ targets: container, scale: 1, duration: 100 }));
    hit.on('pointerdown', () => {
      this.selectedCharacter = character;
      AudioManager.getInstance().playButtonClick();
      this.refreshCharacterSelect();
    });
    return container;
  }

  private refreshCharacterSelect(): void {
    for (const [character, container] of this.characterButtons.entries()) {
      const ring = container.getByName('ring') as Phaser.GameObjects.Arc | undefined;
      ring?.setStrokeStyle(
        character === this.selectedCharacter ? 5 : 3,
        character === this.selectedCharacter ? 0xffcf3f : 0x4dd2cf,
        character === this.selectedCharacter ? 1 : 0.55,
      );
    }
  }

  private createModeCards(): void {
    const save = new SaveStore();
    const snap = save.snapshot();

    this.createStartCard(
      GAME_WIDTH / 2 - 195,
      534,
      'Training Floor',
      COPY.menu.startButton,
      'A top-down AR floor with guided stations, terms, and no desktop busywork.',
      true,
      () => {
        AudioManager.getInstance().playButtonClick();
        this.scene.start('ARFloorScene', { character: this.selectedCharacter });
      },
    );

    this.createStartCard(
      GAME_WIDTH / 2 + 195,
      534,
      'Advanced',
      COPY.menu.endlessButton,
      save.hasCompletedFirstDay()
        ? 'The older desktop crunch mode. Higher pressure, more noise.'
        : 'Finish the Training Floor once to unlock the pressure mode.',
      save.hasCompletedFirstDay(),
      () => {
        if (!save.hasCompletedFirstDay()) return;
        AudioManager.getInstance().playButtonClick();
        this.scene.start('ColdOpenScene', { mode: 'endless' satisfies RunMode });
      },
    );

    if (save.hasCompletedFirstDay()) {
      const stats = [
        `${COPY.menu.daysCompletedLabel}: ${snap.daysCompleted}`,
        `${COPY.menu.bestScoreLabel}: ${snap.bestCraftScore}`,
        snap.bestDso !== null ? `${COPY.menu.bestDsoLabel}: ${snap.bestDso}` : null,
      ].filter((s): s is string => s !== null);

      this.add.text(GAME_WIDTH / 2, 702, stats.join('  |  '), {
        fontFamily: FONTS.ui,
        fontSize: '12px',
        color: toHexColor(MENU.muted),
        fontStyle: '700',
      }).setOrigin(0.5).setName('intro');
    }
  }

  private createStartCard(
    x: number,
    y: number,
    eyebrow: string,
    title: string,
    body: string,
    enabled: boolean,
    onClick: () => void,
  ): void {
    const card = this.add.container(x, y).setName('intro');
    const w = 330;
    const h = 132;

    const shadow = this.add.rectangle(8, 10, w, h, 0x000000, 0.32).setOrigin(0.5);
    card.add(shadow);
    const bg = this.add.rectangle(0, 0, w, h, enabled ? MENU.panel : 0x1d2535, 1)
      .setStrokeStyle(2, enabled ? MENU.panelStroke : 0x48556a, enabled ? 1 : 0.8)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: enabled });
    card.add(bg);

    card.add(this.add.text(-w / 2 + 18, -h / 2 + 16, eyebrow.toUpperCase(), {
      fontFamily: FONTS.ui,
      fontSize: '11px',
      color: enabled ? '#9ee7e1' : '#7b8798',
      fontStyle: '900',
    }));
    card.add(this.add.text(-w / 2 + 18, -h / 2 + 42, title, {
      fontFamily: FONTS.ui,
      fontSize: '20px',
      color: enabled ? '#ffffff' : '#a7b0be',
      fontStyle: '900',
    }));
    card.add(this.add.text(-w / 2 + 18, -h / 2 + 76, body, {
      fontFamily: FONTS.ui,
      fontSize: '12px',
      color: enabled ? toHexColor(MENU.muted) : '#7b8798',
      wordWrap: { width: w - 36, useAdvancedWrap: true },
      lineSpacing: 2,
    }));

    if (enabled) {
      bg.on('pointerover', () => {
        bg.setFillStyle(0x1b3557);
        this.tweens.add({ targets: card, y: y - 4, duration: 100 });
      });
      bg.on('pointerout', () => {
        bg.setFillStyle(MENU.panel);
        this.tweens.add({ targets: card, y, duration: 100 });
      });
      bg.on('pointerdown', onClick);
    }
  }

  private createDecorations(): void {
    this.createCounterScene(132, 662, 0.86);
    this.createCounterScene(888, 662, 0.86);

    for (let i = 0; i < 18; i++) {
      const x = Phaser.Math.Between(90, GAME_WIDTH - 90);
      const y = Phaser.Math.Between(96, GAME_HEIGHT - 95);
      const paper = this.add.rectangle(x, y, 36, 45, MENU.paper, 0.1)
        .setRotation(Phaser.Math.FloatBetween(-0.35, 0.35))
        .setName('intro');
      this.tweens.add({
        targets: paper,
        y: y + Phaser.Math.Between(-12, 12),
        rotation: paper.rotation + Phaser.Math.FloatBetween(-0.12, 0.12),
        duration: 2600 + Phaser.Math.Between(0, 1600),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private createCounterScene(x: number, y: number, scale: number): void {
    const c = this.add.container(x, y).setScale(scale).setName('intro');
    c.add(this.add.rectangle(0, 14, 180, 74, MENU.counterDark).setOrigin(0.5));
    c.add(this.add.rectangle(0, -8, 190, 56, MENU.counter).setOrigin(0.5));
    c.add(this.add.rectangle(-44, -18, 54, 30, 0xffffff).setOrigin(0.5));
    c.add(this.add.rectangle(30, -18, 54, 30, 0xffffff).setOrigin(0.5));
    c.add(this.add.image(72, -20, 'icon:circle-dollar-sign').setDisplaySize(28, 28).setTint(0x102033));
    this.drawCharacter(c, x < GAME_WIDTH / 2 ? 'blue' : 'pink', 0, -74, 0.9);
  }

  private drawCharacter(
    container: Phaser.GameObjects.Container,
    character: Character,
    x: number,
    y: number,
    scale: number,
  ): void {
    const outfit = character === 'blue' ? 0x2f80ed : 0xec4899;
    const hair = character === 'blue' ? 0x3b2f2f : 0x6b3e26;
    const group = this.add.container(x, y).setScale(scale);
    group.add(this.add.ellipse(0, 28, 44, 18, 0x000000, 0.22));
    group.add(this.add.ellipse(0, 20, 42, 52, outfit).setStrokeStyle(3, 0x102033));
    group.add(this.add.circle(0, -16, 20, 0xf6c7a9).setStrokeStyle(3, 0x102033));
    group.add(this.add.ellipse(0, -30, 35, 18, hair));
    group.add(this.add.circle(-7, -18, 3, 0x102033));
    group.add(this.add.circle(7, -18, 3, 0x102033));
    group.add(this.add.rectangle(0, -7, 12, 3, 0x102033));
    group.add(this.add.rectangle(-18, 20, 12, 28, outfit).setRotation(0.45));
    group.add(this.add.rectangle(18, 20, 12, 28, outfit).setRotation(-0.45));
    container.add(group);
  }

  private animateEntrance(): void {
    const objects = this.children.list.filter((o) => o.name === 'intro') as Phaser.GameObjects.GameObject[];
    objects.forEach((o) => {
      const target = o as Phaser.GameObjects.GameObject & { setAlpha?: (a: number) => unknown; y?: number };
      target.setAlpha?.(0);
      if (typeof target.y === 'number') target.y += 14;
    });
    objects.forEach((o, i) => {
      this.tweens.add({
        targets: o,
        alpha: 1,
        y: `-=14`,
        duration: 320,
        delay: i * 18,
        ease: 'Cubic.easeOut',
      });
    });
  }
}
